<script setup>
import { computed, onMounted, reactive, ref, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { runtimeConfig } from '@/config/runtime.js'
import { authStore } from '@/stores/auth.js'
import { workspaceIdentityForRole } from '@/workspaceIdentity.js'

const router = useRouter()
const route = useRoute()
const form = reactive({ email: '', password: '' })
const errorMessage = ref('')
const restoringSession = ref(false)
const entryIdentity = computed(() => workspaceIdentityForRole(route.meta.loginWorkspace))
const showsHierarchy = computed(() => entryIdentity.value.showHierarchy !== false)
const entryLabel = computed(() => showsHierarchy.value
  ? `${entryIdentity.value.level} · ${entryIdentity.value.name}`
  : entryIdentity.value.name)

watchEffect(() => {
  document.title = showsHierarchy.value
    ? `${entryIdentity.value.level} · ${entryIdentity.value.workspace}`
    : entryIdentity.value.workspace
})

function requestedRoute(fallback) {
  return typeof route.query.next === 'string'
    && route.query.next.startsWith('/')
    && !route.query.next.startsWith('//')
    ? route.query.next
    : fallback
}

onMounted(async () => {
  restoringSession.value = true
  try {
    if (await authStore.ensureSession()) {
      const nextRoute = await authStore.prepareDefaultTenant()
      await router.replace(requestedRoute(nextRoute))
    }
  } catch (error) {
    errorMessage.value = error?.message || '暂时无法恢复登录状态，你仍可直接登录。'
  } finally {
    restoringSession.value = false
  }
})

async function submitLogin() {
  errorMessage.value = ''
  try {
    const nextRoute = await authStore.login(
      form.email.trim(),
      form.password,
      route.meta.loginWorkspace,
    )
    form.password = ''
    await router.replace(requestedRoute(nextRoute))
  } catch (error) {
    form.password = ''
    errorMessage.value = error?.message || '暂时无法登录，请稍后重试。'
  }
}
</script>

<template>
  <section class="entry-layout" :class="entryIdentity.key" aria-labelledby="entry-title">
    <div class="entry-copy">
      <span class="workspace-level">{{ entryLabel }}</span>
      <p class="eyebrow">{{ entryIdentity.eyebrow }}</p>
      <h1 id="entry-title">{{ entryIdentity.headline }}</h1>
      <p class="entry-summary">{{ entryIdentity.summary }}</p>

      <ul class="promise-list" :aria-label="`${entryIdentity.name}权限范围`">
        <li v-for="(promise, index) in entryIdentity.promises" :key="promise"><span aria-hidden="true">0{{ index + 1 }}</span> {{ promise }}</li>
      </ul>
    </div>

    <div class="entry-card" :aria-label="`${entryIdentity.name}登录入口`">
      <div class="entry-card-icon" aria-hidden="true">{{ entryIdentity.mark }}</div>
      <span v-if="showsHierarchy" class="entry-card-level">{{ entryIdentity.level }}</span>
      <h2>{{ entryIdentity.cardTitle }}</h2>
      <p>{{ entryIdentity.cardSummary }}</p>
      <p v-if="restoringSession" class="session-restoring" role="status">正在恢复已有登录状态…</p>
      <form class="login-form" @submit.prevent="submitLogin">
        <label>
          <span>邮箱</span>
          <input
            v-model="form.email"
            type="email"
            name="email"
            autocomplete="username"
            inputmode="email"
            required
            maxlength="255"
            :placeholder="entryIdentity.emailPlaceholder"
          />
        </label>
        <label>
          <span>密码</span>
          <input
            v-model="form.password"
            type="password"
            name="password"
            autocomplete="current-password"
            required
            maxlength="128"
            placeholder="请输入密码"
          />
        </label>
        <p v-if="errorMessage" class="form-error" role="alert">{{ errorMessage }}</p>
        <button type="submit" :disabled="authStore.state.busy">
          {{ authStore.state.busy ? '正在安全登录…' : entryIdentity.button }}
        </button>
      </form>
      <div class="connection-row">
        <span class="connection-dot" aria-hidden="true"></span>
        <span>{{ runtimeConfig.apiMode === 'same-origin' ? '同源云端连接已就绪' : 'HTTPS 云端连接已就绪' }}</span>
      </div>
      <small>账号、企业和项目全部以云端实时权限为准。</small>
    </div>
  </section>
</template>
